/**
 * Returning-session "your budget reset" banner (mvp CST4).
 *
 * The daily free AI budget exists to bring users back, not to wall them out. The
 * limit-hit surfaces (toast / in-chat card / {@link UsageNudge}) already LEAD with
 * when the budget refreshes; this is the positive other half — when a user who
 * previously hit the daily cap returns AFTER it has refreshed, a friendly banner
 * confirms they have the full free budget again, closing the retention loop.
 *
 * How it knows: the HTTP limit-error interceptor (`bonds/app-http.ts`) records the
 * UTC day-bucket of the last daily AI-cap hit under {@link AI_BUDGET_HIT_DAY_KEY}
 * via the `@molecule/app-storage` adapter. On mount this reads that marker and, if
 * {@link budgetHasResetSince} reports at least one daily reset has elapsed since,
 * shows the welcome-back banner once. Dismissing (or the banner showing) clears the
 * marker so it never re-shows for the same reset; if the user hits the cap again the
 * interceptor re-arms it with the new day.
 *
 * **Mutual exclusion with {@link UsageNudge}:** the marker alone is a stale
 * signal — the user can have re-hit (or be near) a limit in the meantime, and
 * "your budget refreshed" beside "you've used today's budget" is a
 * contradiction. So this banner also consults the LIVE usage rollup (the same
 * {@link useAccountUsage} the nudge reads) and stays hidden whenever any
 * metric is tight (severity ≠ 'none') — i.e. whenever the nudge has something
 * to warn about.
 *
 * **Free plan only:** the copy is about the FREE daily budget, but the marker
 * is recorded for any `ai_cost`/`ai_messages` limit hit — including a paid
 * tier's billing-period allowance, which does NOT refresh daily — and the user
 * can also upgrade between the hit and the return. So the banner shows only
 * when the live rollup confirms the free plan (`plan == null`); an unreadable
 * rollup can't confirm it and stays quiet.
 *
 * All decision math is the pure {@link budgetHasResetSince} in
 * `utilities/usage-utilities.ts`; this component is the thin presentational shell.
 * Both layouts render through the shared {@link Banner}:
 * - `variant="banner"` (default) — full card for the Dashboard.
 * - `variant="compact"` — a thin strip for the Workspace.
 */

import { useCallback, useEffect, useState } from 'react'

import { getProvider as getStorageProvider } from '@molecule/app-storage'

import { useAccountUsage } from '../hooks/useAccountUsage.js'
import { budgetResetWelcomeBack } from '../upgrade-copy.js'
import {
  AI_BUDGET_HIT_DAY_KEY,
  budgetHasResetSince,
  nudgeStateFromUsage,
} from '../utilities/usage-utilities.js'
import { Banner } from './Banner.js'

interface BudgetResetBannerProps {
  /** Layout: full card for the Dashboard, thin strip for the Workspace. */
  variant?: 'banner' | 'compact'
}

export function BudgetResetBanner({ variant = 'banner' }: BudgetResetBannerProps) {
  const { usage, settled: usageSettled } = useAccountUsage()

  // `undefined` = marker not yet read from storage (don't flash before we know);
  // `true`/`false` = whether the budget has reset since the last recorded hit.
  const [shouldShow, setShouldShow] = useState<boolean | undefined>(undefined)

  // Read the recorded hit-day marker and decide whether the budget has since
  // refreshed. Silent on failure — a positive banner is purely additive.
  useEffect(() => {
    let cancelled = false
    let provider: ReturnType<typeof getStorageProvider>
    try {
      provider = getStorageProvider()
    } catch (_error) {
      // Storage bond not configured (very early boot / a context without storage):
      // `getProvider()` throws synchronously, and a purely-additive reset banner must
      // never crash the page that hosts it — degrade to hidden.
      setShouldShow(false)
      return
    }
    provider
      .get<number>(AI_BUDGET_HIT_DAY_KEY)
      .then((hitDay) => {
        if (!cancelled) setShouldShow(budgetHasResetSince(hitDay))
      })
      .catch(() => {
        if (!cancelled) setShouldShow(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Clear the marker and hide — shown once per reset; re-arms only if the user
  // hits the cap again (the interceptor rewrites the marker with the new day).
  const dismiss = useCallback(() => {
    setShouldShow(false)
    try {
      getStorageProvider()
        .remove(AI_BUDGET_HIT_DAY_KEY)
        .catch(() => {
          // Non-fatal: in-session state already hides it.
        })
    } catch (_error) {
      // No storage provider configured — in-session state already hides it.
    }
  }, [])

  if (shouldShow !== true) return null
  // Wait until the live usage fetch has resolved before cheering. While it is
  // in flight `usage` is `null`, which reads as "nothing tight" — so showing
  // now would flash "budget refreshed" for the load window and then rip it away
  // the instant a spent-budget rollup arrives (the guest-refresh flicker).
  if (!usageSettled) return null
  // Only the FREE plan has a daily budget that refreshes — a paying user's
  // allowance meters over their billing period, and the marker may predate an
  // upgrade. Require the rollup to confirm the free plan; unreadable = quiet.
  if (usage == null || usage.plan != null) return null
  // Live usage shows something tight → the UsageNudge (or a limit card) owns
  // this moment; a "budget refreshed" cheer would contradict it.
  if (nudgeStateFromUsage(usage).severity !== 'none') return null

  return (
    <Banner
      tone="success"
      variant={variant === 'compact' ? 'strip' : 'card'}
      molId={`budget-reset-banner-${variant}`}
      message={budgetResetWelcomeBack()}
      onDismiss={dismiss}
      dismissMolId="budget-reset-banner-dismiss"
      style={variant === 'banner' ? { marginBottom: 32 } : undefined}
      role="status"
    />
  )
}
